import { Button, FormControlLabel, IconButton, List, ListItem, ListItemSecondaryAction, ListItemText, Switch, TextField } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ReusableSelect as Select } from 'components/reusable/select';
import { setMessages } from 'store/actions/actions';
import { useGlobalState } from 'store/reducers/reducer';
import { adminDisableAccount, adminLoadAccount, adminUpdateAccount, loadAccountProperties, loadAccounts } from 'store/sagas/sagas';
import { SUBSCRIPTION_TIERS } from 'utils/billing';
import { isURL } from 'utils/url';
import './account-form.css';
const MIN_SMS_INVITE_TIME = 3 * 60;
const MIN_EMAIL_INVITE_TIME = 15 * 60;
const LOGO_MAX_WIDTH = 800;
const LOGO_MAX_HEIGHT_AFTER_RESIZE = 1000;
const resizeImageToDataUri = async (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('error', () => {
        reject(new Error('Failed to read file'));
    });
    reader.addEventListener('load', () => {
        const img = new Image();
        img.addEventListener('error', (ev) => {
            reject(new Error('Failed to load image', { cause: ev.error }));
        });
        img.addEventListener('load', () => {
            const scale = Math.min(1, LOGO_MAX_WIDTH / img.width);
            const width = Math.round(img.width * scale);
            const height = Math.round(img.height * scale);
            if (height > LOGO_MAX_HEIGHT_AFTER_RESIZE) {
                reject(new Error('Image must be roughly square or wide'));
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Canvas not supported'));
                return;
            }
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/png'));
        });
        img.src = reader.result;
    });
    reader.readAsDataURL(file);
});
const validateFormBeforeSubmit = (form) => {
    const { url, inviteRetentionTimeEmail, inviteRetentionTimeSms } = form;
    // check if url value is a valid value
    if (url && !isURL(url)) {
        return `the URL format is incorrect`;
    }
    if (typeof inviteRetentionTimeEmail === 'number' &&
        inviteRetentionTimeEmail < MIN_EMAIL_INVITE_TIME) {
        return `Email invite expiration time must be at least ${MIN_EMAIL_INVITE_TIME / 60} minutes`;
    }
    if (typeof inviteRetentionTimeSms === 'number' &&
        inviteRetentionTimeSms < MIN_SMS_INVITE_TIME) {
        return `SMS invite expiration time must be at least ${MIN_SMS_INVITE_TIME / 60} minutes`;
    }
    return undefined;
};
const useAccountSetter = (setter, property, allowNull) => {
    return (ev) => {
        let value = ev.currentTarget.valueAsNumber;
        if (Number.isNaN(value) || value < 0) {
            if (!allowNull) {
                return;
            }
            value = null;
        }
        setter((prev) => {
            if (!prev) {
                return null;
            }
            return {
                ...prev,
                [property]: value
            };
        });
    };
};
const accountSetupSteps = [
    {
        id: 'TERMS',
        name: 'Terms'
    },
    {
        id: 'BILLING',
        name: 'Billing'
    },
    {
        id: 'COMPANY',
        name: 'Company'
    },
    {
        id: 'COMPLETE',
        name: 'Complete'
    },
    {
        id: 'null',
        name: 'None'
    }
];
const setupStatuses = [
    {
        id: 'INCOMPLETE_ACCOUNT_INFO',
        name: 'Incomplete Account Info'
    },
    {
        id: 'UNVERIFIED_EMAIL',
        name: 'Unverified Email'
    },
    {
        id: 'COMPLETED',
        name: 'Complete'
    }
];
export const AccountForm = () => {
    const { asyncDispatch, dispatch, state } = useGlobalState();
    const navigate = useNavigate();
    const [account, setAccount] = useState(null);
    const [secretClientKey, setSecretClientKey] = useState('');
    const [logo, setLogo] = useState(undefined);
    const [currentLogoUrl, setCurrentLogoUrl] = useState(undefined);
    const [childSearchTerm, setChildSearchTerm] = useState('');
    const [selectedChildId, setSelectedChildId] = useState(undefined);
    const [isUpdatingChildAccounts, setIsUpdatingChildAccounts] = useState(false);
    const [ial2ComplianceEnabled, setIal2ComplianceEnabled] = useState(false);
    const { id } = useParams();
    useEffect(() => {
        setIal2ComplianceEnabled(false);
        setLogo(undefined);
        setCurrentLogoUrl(undefined);
        asyncDispatch(adminLoadAccount(id)).then(setAccount).catch(console.error);
        asyncDispatch(loadAccountProperties(id))
            .then((data) => {
            const props = data.configurationProperties?.props;
            if (props) {
                setIal2ComplianceEnabled(!!props.nist?.ial2ComplianceEnabled);
                setCurrentLogoUrl(props.nowWebApp?.theme?.src);
            }
        })
            .catch(console.error);
        // Reset child-linking local state when switching accounts.
        setSelectedChildId(undefined);
        setChildSearchTerm('');
    }, [id]);
    // Load all accounts for the admin dropdown if not already loaded
    useEffect(() => {
        if (!state.accounts) {
            asyncDispatch(loadAccounts()).catch(console.error);
        }
    }, [state.accounts]);
    // Clear selectedChildId if the selected account is no longer available
    useEffect(() => {
        if (!selectedChildId || !state.accounts || !account) {
            return;
        }
        const selectedAccount = state.accounts.find((a) => a.id === selectedChildId);
        const isStillAvailable = selectedAccount &&
            !selectedAccount.isDisabled &&
            selectedAccount.parentId === null &&
            selectedAccount.id !== account.id;
        if (!isStillAvailable) {
            setSelectedChildId(undefined);
        }
    }, [selectedChildId, state.accounts, account]);
    const removeChildAccount = useCallback((childId, childName) => {
        if (
        // eslint-disable-next-line no-alert
        !window.confirm(`Are you sure you want to remove "${childName}" as a child account?`)) {
            return;
        }
        setIsUpdatingChildAccounts(true);
        asyncDispatch(adminUpdateAccount(childId, { parentId: null }))
            .then(async () => {
            dispatch(setMessages({
                value: `Child account "${childName}" removed successfully`,
                severity: 'success'
            }));
            return asyncDispatch(loadAccounts());
        })
            .catch((error) => {
            console.error(error);
            dispatch(setMessages({
                value: 'Failed to remove child account',
                severity: 'error'
            }));
        })
            .finally(() => {
            setIsUpdatingChildAccounts(false);
        });
    }, [dispatch]);
    const addChildAccount = useCallback(() => {
        if (!selectedChildId || !account || !state.accounts) {
            return;
        }
        const childAccount = state.accounts.find((a) => a.id === selectedChildId);
        if (!childAccount) {
            return;
        }
        // Prevent circular reference: can't add our parent as a child
        if (account.parentId === selectedChildId) {
            dispatch(setMessages({
                value: 'Cannot add parent account as a child',
                severity: 'error'
            }));
            return;
        }
        if (childAccount.parentId &&
            childAccount.parentId !== account.id &&
            // eslint-disable-next-line no-alert
            !window.confirm(`"${childAccount.name}" already has a parent account. Do you want to reassign it to "${account.name}"?`)) {
            return;
        }
        setIsUpdatingChildAccounts(true);
        asyncDispatch(adminUpdateAccount(selectedChildId, { parentId: account.id }))
            .then(async () => {
            dispatch(setMessages({
                value: `"${childAccount.name}" added as a child account`,
                severity: 'success'
            }));
            setSelectedChildId(undefined);
            return asyncDispatch(loadAccounts());
        })
            .catch((error) => {
            console.error(error);
            dispatch(setMessages({
                value: 'Failed to add child account',
                severity: 'error'
            }));
        })
            .finally(() => {
            setIsUpdatingChildAccounts(false);
        });
    }, [account, dispatch, selectedChildId, state.accounts]);
    const updateAccount = () => {
        if (!account) {
            return;
        }
        const data = {
            firstName: account.firstName,
            lastName: account.lastName,
            name: account.name,
            url: account.url,
            email: account.email,
            phoneNumber: account.phoneNumber,
            setupAccountStep: account.setupAccountStep,
            setupStatus: account.setupStatus,
            companySize: account.companySize,
            city: account.city,
            province: account.province,
            country: account.country,
            industry: account.industry,
            isDisabled: account.isDisabled,
            isTest: account.isTest,
            isInternal: account.isInternal,
            stripeCustomerId: account.stripeCustomerId,
            subscriptionId: account.subscriptionId,
            subscriptionTier: account.subscriptionTier,
            retentionPolicy: account.retentionPolicy,
            hasAnalyticsAccess: account.hasAnalyticsAccess,
            candidateContactInfoRequired: account.candidateContactInfoRequired,
            inviteRetentionTimeSms: account.inviteRetentionTimeSms,
            inviteRetentionTimeEmail: account.inviteRetentionTimeEmail,
            legacyWebhooks: account.legacyWebhooks
        };
        const error = validateFormBeforeSubmit(data);
        if (error) {
            dispatch(setMessages({
                value: error,
                severity: 'error'
            }));
            return;
        }
        // overwrite url param
        if (!data.url) {
            data.url = 'https://www.vouched.id/';
        }
        asyncDispatch(adminUpdateAccount(account.id, {
            ...data,
            ...(secretClientKey ? { secretClientKey } : {}),
            ...(logo ? { logo } : {})
        })).then(() => {
            dispatch(setMessages({
                value: 'Changes saved',
                severity: 'success'
            }));
        }, console.error);
    };
    const handleLogoUpload = useCallback((e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) {
            return;
        }
        resizeImageToDataUri(file)
            .then(setLogo)
            .catch((error) => {
            console.error(error);
            dispatch(setMessages({
                value: 'Failed to process logo image',
                severity: 'error'
            }));
        });
    }, [dispatch]);
    const handleClearLogo = useCallback(() => {
        setLogo(undefined);
    }, []);
    const handleGoToAccountProperties = useCallback(() => {
        navigate(`/admin/accounts/${id}/properties`);
    }, [id, navigate]);
    const handleGoToGroupConfig = useCallback(() => {
        navigate(`/admin/accounts/${id}/groupconfig`);
    }, [id, navigate]);
    const handleGoToVendorKeys = useCallback(() => {
        navigate(`/admin/accounts/${id}/vendor-keys`);
    }, [id, navigate]);
    const handleName = useCallback((e) => {
        setAccount((a) => ({ ...a, name: e.target.value }));
    }, []);
    const handleEmail = useCallback((e) => {
        setAccount((a) => ({ ...a, email: e.target.value }));
    }, []);
    const handleLastName = useCallback((e) => {
        setAccount((a) => ({ ...a, lastName: e.target.value }));
    }, []);
    const handleFirstName = useCallback((e) => {
        setAccount((a) => ({ ...a, firstName: e.target.value }));
    }, []);
    const handleUrl = useCallback((e) => {
        setAccount((a) => ({ ...a, url: e.target.value }));
    }, []);
    const handlePhoneNumber = useCallback((e) => {
        setAccount((a) => ({ ...a, phoneNumber: e.target.value }));
    }, []);
    const handleSetupAccountStep = useCallback((e) => {
        setAccount((a) => ({
            ...a,
            setupAccountStep: e.target.value === 'null' ? null : e.target.value
        }));
    }, []);
    const handleSetupStatus = useCallback((e) => {
        setAccount((a) => ({ ...a, setupStatus: e.target.value }));
    }, []);
    const handleCountry = useCallback((e) => {
        setAccount((a) => ({ ...a, country: e.target.value }));
    }, []);
    const handleCity = useCallback((e) => {
        setAccount((a) => ({ ...a, city: e.target.value }));
    }, []);
    const handlePostalCode = useCallback((e) => {
        setAccount((a) => ({ ...a, postalCode: e.target.value }));
    }, []);
    const handleProvince = useCallback((e) => {
        setAccount((a) => ({ ...a, province: e.target.value }));
    }, []);
    const handleCompanySize = useCallback((e) => {
        setAccount((a) => ({ ...a, companySize: e.target.value }));
    }, []);
    const handleStripeCustomerId = useCallback((e) => {
        setAccount((a) => ({ ...a, stripeCustomerId: e.target.value }));
    }, []);
    const handleSubscriptionTier = useCallback((e) => {
        setAccount((a) => ({ ...a, subscriptionTier: e.target.value }));
    }, []);
    const handleIndustry = useCallback((e) => {
        setAccount((a) => ({ ...a, industry: e.target.value }));
    }, []);
    const handleSecretClientKey = useCallback((e) => {
        setSecretClientKey(e.target.value);
    }, []);
    const handleSetDisabled = useCallback((_e, checked) => {
        setAccount((a) => ({ ...a, isDisabled: checked }));
    }, []);
    const handleSetActiveBilling = useCallback((_e, checked) => {
        setAccount((a) => ({ ...a, hasActiveBilling: checked }));
    }, []);
    const handleHasAnalyticsAccess = useCallback((_e, checked) => {
        setAccount((a) => ({ ...a, hasAnalyticsAccess: checked }));
    }, []);
    const handleTest = useCallback((_e, checked) => {
        if (
        // eslint-disable-next-line no-alert
        window.prompt('Warning: Marking an account as test is PERMANENT, please enter the customer name to proceed.') !== account?.name) {
            // eslint-disable-next-line no-alert
            alert('Incorrect account name');
            return;
        }
        setAccount((a) => ({ ...a, isTest: checked }));
    }, [account?.name]);
    const handleInternal = useCallback((_e, checked) => {
        if (
        // eslint-disable-next-line no-alert
        !window.confirm('Warning: Marking an account as internal will disable all model training on its jobs.')) {
            return;
        }
        setAccount((a) => ({ ...a, isInternal: checked }));
    }, []);
    const handleCandidateContactInfoRequired = useCallback((_e, checked) => {
        setAccount((a) => ({ ...a, candidateContactInfoRequired: checked }));
    }, []);
    const handleRetentionPolicy = useAccountSetter(setAccount, 'retentionPolicy', false);
    const handleRetentionPolicyEnabled = useCallback((e) => {
        if (e.currentTarget.checked &&
            // eslint-disable-next-line no-alert
            !confirm('Enabling retention policy will permanently delete candidate data after the specified number of days. Are you sure you want to enable it?')) {
            return;
        }
        setAccount((a) => ({
            ...a,
            retentionPolicy: e.target.checked ? 14 : null
        }));
    }, []);
    const confirmDisableMessage = "This will cancel the account's subscription and disable the account. " +
        'Are you sure you want to do this?';
    const disableAccount = () => {
        // eslint-disable-next-line no-alert
        if (window.confirm(confirmDisableMessage)) {
            asyncDispatch(adminDisableAccount(id))
                .then((data) => {
                setAccount((a) => ({
                    ...a,
                    isDisabled: data.isDisabled,
                    subscriptionId: data.subscriptionId ?? '',
                    hasActiveBilling: data.hasActiveBilling
                }));
                dispatch(setMessages({
                    value: `Account has been disabled`,
                    severity: 'success'
                }));
            })
                .catch(console.error);
        }
    };
    const handleInviteRetentionTimeSms = useAccountSetter(setAccount, 'inviteRetentionTimeSms', true);
    const handleInviteRetentionTimeEmail = useAccountSetter(setAccount, 'inviteRetentionTimeEmail', true);
    const handleLegacyWebhooks = useCallback((_e, checked) => {
        setAccount((a) => ({ ...a, legacyWebhooks: checked }));
    }, []);
    const handleChildSearchChange = useCallback((e) => {
        setChildSearchTerm(e.target.value);
    }, []);
    const handleChildSelectChange = useCallback((e) => {
        setSelectedChildId(e.target.value || undefined);
    }, []);
    if (!account) {
        return React.createElement(React.Fragment, null, "Loading...");
    }
    // Derive parent name and child accounts from state.accounts
    const parentAccountName = state.accounts?.find((a) => a.id === account.parentId)?.name ?? '';
    const childAccounts = (state.accounts ?? []).filter((a) => a.parentId === account.id);
    // Filter to enabled accounts without a parent and without children, excluding self
    const searchLower = childSearchTerm.toLowerCase();
    const accountIdsWithChildren = new Set((state.accounts ?? [])
        .filter((a) => a.parentId !== null)
        .map((a) => a.parentId));
    const availableChildAccounts = (state.accounts ?? []).filter((a) => !a.isDisabled &&
        a.parentId === null &&
        a.id !== account.id &&
        !accountIdsWithChildren.has(a.id) &&
        (!childSearchTerm || a.name.toLowerCase().includes(searchLower)));
    return (React.createElement("div", { className: "account-form" },
        React.createElement("h1", { className: "u-margin-bottom-medium" },
            "Update Account - ",
            account.name),
        React.createElement("h2", { className: "u-margin-bottom-small" }, "System"),
        React.createElement("div", { style: { display: 'flex', gap: '2rem' } },
            React.createElement(TextField, { label: "ID", value: account.id }),
            React.createElement(TextField, { label: "SID", value: account.sid })),
        account.parentId !== null && (React.createElement(React.Fragment, null,
            React.createElement("h2", { className: "u-margin-bottom-small" }, "Parent Account"),
            React.createElement("div", { style: { display: 'flex', gap: '2rem', alignItems: 'center' } },
                React.createElement(TextField, { inputProps: { readOnly: true }, label: "Parent ID", value: account.parentId }),
                React.createElement(TextField, { inputProps: { readOnly: true }, label: "Parent Name", value: parentAccountName }),
                React.createElement(Button, { onClick: () => {
                        navigate(`/admin/accounts/${account.parentId}`);
                    } }, "View Parent Account")))),
        account.parentId === null && (React.createElement(React.Fragment, null,
            React.createElement("h2", { className: "u-margin-bottom-small" },
                "Child Accounts (",
                childAccounts.length,
                ")"),
            childAccounts.length > 0 ? (React.createElement(List, { dense: true }, childAccounts.map((child) => (React.createElement(ListItem, { button: true, key: child.id, onClick: () => {
                    navigate(`/admin/accounts/${child.id}`);
                } },
                React.createElement(ListItemText, { primary: `${child.name} (ID: ${child.id})`, secondary: React.createElement("span", { "data-ph-mask": true }, child.email) }),
                React.createElement(ListItemSecondaryAction, null,
                    React.createElement(IconButton, { "aria-label": "remove", disabled: isUpdatingChildAccounts, edge: "end", onClick: (e) => {
                            e.stopPropagation();
                            removeChildAccount(child.id, child.name);
                        }, size: "small" },
                        React.createElement(DeleteIcon, null)))))))) : (React.createElement("div", { style: { color: '#666' } }, "No child accounts")),
            React.createElement("div", { style: {
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    alignItems: 'flex-end',
                    marginBottom: '1rem'
                } },
                React.createElement(TextField, { label: "Search accounts by name", onChange: handleChildSearchChange, value: childSearchTerm }),
                React.createElement(Select, { className: "g-full-width", items: availableChildAccounts.map((a) => ({
                        id: a.id,
                        name: `${a.name} (ID: ${a.id})`
                    })), label: "Add child account", onChange: handleChildSelectChange, value: selectedChildId }),
                React.createElement(Button, { color: "primary", disabled: !selectedChildId || isUpdatingChildAccounts, onClick: addChildAccount, variant: "contained" }, "Add as Child")))),
        React.createElement("h2", { className: "u-margin-bottom-small" }, "Contact"),
        React.createElement(TextField, { label: "First name", onChange: handleFirstName, value: account.firstName ?? '' }),
        React.createElement(TextField, { label: "Last name", onChange: handleLastName, value: account.lastName ?? '' }),
        React.createElement(TextField, { label: "Email", onChange: handleEmail, value: account.email ?? '' }),
        React.createElement(TextField, { label: "City", onChange: handleCity, value: account.city ?? '' }),
        React.createElement(TextField, { label: "Postal Code", onChange: handlePostalCode, value: account.province ?? '' }),
        React.createElement(TextField, { label: "Country", onChange: handleCountry, value: account.country ?? '' }),
        React.createElement(TextField, { label: "Province", onChange: handleProvince, value: account.province ?? '' }),
        React.createElement(TextField, { label: "Industry", onChange: handleIndustry, value: account.industry ?? '' }),
        React.createElement(TextField, { label: "Company Size", onChange: handleCompanySize, value: account.companySize ?? '' }),
        React.createElement(TextField, { label: "Phone Number", onChange: handlePhoneNumber, value: account.phoneNumber ?? '' }),
        React.createElement(TextField, { label: "URL", onChange: handleUrl, value: account.url }),
        React.createElement("h2", { className: "u-margin-bottom-small" }, "Settings"),
        React.createElement(TextField, { helperText: "Account name to be used in various parts of the system including customer emails.", label: "Name", onChange: handleName, value: account.name }),
        React.createElement(Select, { helperText: "Current step in the account/payment provisioning.", isClearable: false, items: accountSetupSteps, label: "Setup Account Step", onChange: handleSetupAccountStep, value: account.setupAccountStep ?? 'null' }),
        React.createElement(Select, { helperText: "Current status of the initial account creation.", isClearable: false, items: setupStatuses, label: "Setup Status", onChange: handleSetupStatus, value: account.setupStatus }),
        React.createElement(TextField, { helperText: `'${SUBSCRIPTION_TIERS.SELF_SERVICE}' is self service, 'xr7a66Vmv' is legacy, 'trial' is trial period, other values have no technical significance.`, label: "Subscription Tier", onChange: handleSubscriptionTier, value: account.subscriptionTier ?? '' }),
        React.createElement(TextField, { helperText: "Stripe Customer ID associated with this account for billing purposes.", label: "Stripe Customer ID", onChange: handleStripeCustomerId, value: account.stripeCustomerId ?? '' }),
        React.createElement(TextField, { error: typeof account.inviteRetentionTimeSms === 'number' &&
                account.inviteRetentionTimeSms < MIN_SMS_INVITE_TIME, 
            // eslint-disable-next-line max-len
            helperText: `After how many seconds sms invite links expires. Set to 0 or empty for no expiration, NIST standard is 10min (600s). Minimum is ${Math.floor(MIN_SMS_INVITE_TIME / 60)} min (${MIN_SMS_INVITE_TIME}s)`, inputProps: { min: MIN_SMS_INVITE_TIME }, label: "SMS invite expiration time", onChange: handleInviteRetentionTimeSms, type: "number", value: account.inviteRetentionTimeSms ?? '' }),
        React.createElement(TextField, { error: typeof account.inviteRetentionTimeEmail === 'number' &&
                account.inviteRetentionTimeEmail < MIN_EMAIL_INVITE_TIME, 
            // eslint-disable-next-line max-len
            helperText: `After how many seconds email invite links expires. Set to 0 or empty for no expiration, NIST standard is 24h (86400s). Minimum is ${Math.floor(MIN_EMAIL_INVITE_TIME / 60)} min (${MIN_EMAIL_INVITE_TIME}s)`, inputProps: { min: MIN_EMAIL_INVITE_TIME }, label: "Email invite expiration time", onChange: handleInviteRetentionTimeEmail, type: "number", value: account.inviteRetentionTimeEmail ?? '' }),
        React.createElement(FormControlLabel, { control: React.createElement(Switch, { checked: !!account.retentionPolicy, className: "form-checkbox", disabled: ial2ComplianceEnabled, onChange: handleRetentionPolicyEnabled }), label: ial2ComplianceEnabled
                ? 'Retention policy enabled (required by IAL2 Compliance)'
                : 'Retention policy enabled' }),
        account.retentionPolicy !== null &&
            account.retentionPolicy !== undefined && (React.createElement(TextField, { helperText: "Number of days to retain candidate data before permanent deletion, note that deletion is running on a schedule and is not immediate. Minimum is 1 day.", inputProps: { step: 1, min: 1 }, label: "Retention Policy Days", onChange: handleRetentionPolicy, type: "number", value: account.retentionPolicy })),
        React.createElement(FormControlLabel, { control: React.createElement(Switch, { checked: !!account.hasActiveBilling, disabled: true, onChange: handleSetActiveBilling, size: "medium" }), label: "Has Active Billing" }),
        React.createElement(FormControlLabel, { control: React.createElement(Switch, { checked: account.legacyWebhooks, onChange: handleLegacyWebhooks, size: "medium" }), label: "Enables legacy webhooks which do not filter to rest api format" }),
        React.createElement(FormControlLabel, { control: React.createElement(Switch, { checked: account.isDisabled, onChange: handleSetDisabled, size: "medium" }), label: "Disabled" }),
        React.createElement(FormControlLabel, { control: React.createElement(Switch, { checked: account.hasAnalyticsAccess, onChange: handleHasAnalyticsAccess, size: "medium" }), label: "Analytics Access" }),
        React.createElement(FormControlLabel, { control: React.createElement(Switch, { checked: account.candidateContactInfoRequired, onChange: handleCandidateContactInfoRequired }), label: "Candidate creation requires email AND phone number" }),
        React.createElement(FormControlLabel, { control: React.createElement(Switch, { checked: account.isTest, disabled: account.isTest, onChange: handleTest, size: "medium" }), label: React.createElement(React.Fragment, null,
                "Known test account, job data may not be sent to external partners, results may be simulated. See",
                ' ',
                React.createElement("a", { href: "https://docs.vouched.id/docs/test-accounts", rel: "noopener noreferrer", target: "_blank" }, "docs for more info"),
                ".") }),
        React.createElement(FormControlLabel, { control: React.createElement(Switch, { checked: account.isInternal, disabled: account.isInternal, onChange: handleInternal, size: "medium" }), label: "Known internal test/demo account" }),
        React.createElement(TextField, { FormHelperTextProps: {
                style: {
                    fontSize: '1rem'
                }
            }, helperText: "This field will not load the actual secret key but if you enter anything and hit update, it will update the user's key", label: "Secret Client Key", multiline: true, onChange: handleSecretClientKey, value: secretClientKey }),
        React.createElement("h2", { className: "u-margin-bottom-small" }, "Logo"),
        React.createElement("div", { style: { display: 'flex', gap: '1rem', alignItems: 'center' } },
            React.createElement(Button, { component: "label", variant: "outlined" },
                "Choose Logo",
                React.createElement("input", { accept: "image/png, image/jpeg", hidden: true, onChange: handleLogoUpload, type: "file" })),
            logo ? (React.createElement(React.Fragment, null,
                React.createElement("img", { alt: "New logo preview", src: logo, style: {
                        border: '1px solid #ccc',
                        maxHeight: 200,
                        maxWidth: 200
                    } }),
                React.createElement(Button, { onClick: handleClearLogo }, "Clear new logo"))) : currentLogoUrl ? (React.createElement("img", { alt: "Current logo", src: currentLogoUrl, style: {
                    border: '1px solid #ccc',
                    maxHeight: 200,
                    maxWidth: 200
                } })) : (React.createElement("span", { style: { color: '#666' } }, "No logo set"))),
        React.createElement("h2", null, "Manage Account Properties and Billing"),
        React.createElement(Button, { onClick: handleGoToAccountProperties }, "Manage Properties"),
        React.createElement(Button, { onClick: handleGoToGroupConfig }, "Manage Stable Pattern"),
        React.createElement(Button, { onClick: handleGoToVendorKeys }, "Manage SDK Vendor Keys"),
        React.createElement(Button, { onClick: disableAccount }, "Cancel Subscription"),
        React.createElement("h2", null, "Actions"),
        React.createElement(Button, { onClick: updateAccount }, "Save Changes"),
        React.createElement("p", null,
            "For additional info please see",
            ' ',
            React.createElement("a", { href: "http://go/account-settings" }, "this documentation"),
            ".")));
};
